import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloudServerOutlined, ReloadOutlined } from '@ant-design/icons';
import { del, get, post, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './StorageConfigPage.css';

interface StorageProvider {
  id: number;
  name: string;
  provider_type: string;
  endpoint: string;
  public_endpoint: string;
  access_key: string;
  bucket: string;
  region: string;
  secure: boolean;
  status: number;
  is_default: boolean;
  description: string;
  has_secret_key: boolean;
  updated_at?: string;
}

interface StorageFormValues {
  name: string;
  provider_type: string;
  endpoint: string;
  public_endpoint?: string;
  access_key?: string;
  secret_key?: string;
  bucket: string;
  region?: string;
  secure?: boolean;
  status?: boolean;
  description?: string;
}

const providerLabels: Record<string, string> = {
  minio: 'MinIO',
  s3: 'S3兼容',
  custom: '自定义 S3',
  file_service: '公司文件服务',
};

const StorageConfigPage: React.FC = () => {
  const [items, setItems] = useState<StorageProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StorageProvider | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<StorageFormValues>();

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const data = await get<StorageProvider[]>(API_ENDPOINTS.storage.providers);
      setItems(data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载云存储配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const openEdit = (record: StorageProvider) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      provider_type: record.provider_type,
      endpoint: record.endpoint,
      public_endpoint: record.public_endpoint,
      access_key: record.access_key,
      secret_key: '',
      bucket: record.bucket,
      region: record.region,
      secure: record.secure,
      status: record.status === 1,
      description: record.description,
    });
    setModalOpen(true);
  };

  const submitForm = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      public_endpoint: values.public_endpoint || values.endpoint,
      status: values.status ? 1 : 0,
    };
    setSaving(true);
    try {
      if (editing) {
        await put(API_ENDPOINTS.storage.providerDetail(editing.id), payload);
        message.success('云存储配置已保存');
      } else {
        await post(API_ENDPOINTS.storage.providers, payload);
        message.success('云存储配置已添加');
      }
      setModalOpen(false);
      fetchProviders();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存云存储配置失败');
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (record: StorageProvider) => {
    try {
      await post(API_ENDPOINTS.storage.setDefault(record.id));
      message.success(`系统存储已切换到“${record.name}”`);
      fetchProviders();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '切换默认云存储失败');
    }
  };

  const testProvider = async (record: StorageProvider) => {
    try {
      const result = await post<{ ok: boolean; message: string }>(API_ENDPOINTS.storage.test(record.id));
      if (result.ok) {
        message.success(result.message);
      } else {
        message.warning(result.message);
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : '测试云存储失败');
    }
  };

  const deleteProvider = async (record: StorageProvider) => {
    try {
      await del(API_ENDPOINTS.storage.providerDetail(record.id));
      message.success('云存储配置已删除');
      fetchProviders();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除云存储配置失败');
    }
  };

  const columns: ColumnsType<StorageProvider> = [
    {
      title: '名称',
      dataIndex: 'name',
      width: 150,
      render: (value, record) => (
        <Space size={6}>
          <span>{value}</span>
          {record.is_default && <Tag color="green">默认</Tag>}
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'provider_type',
      width: 100,
      render: (value) => <Tag color={value === 'minio' ? 'blue' : value === 'file_service' ? 'cyan' : 'purple'}>{providerLabels[value] || value}</Tag>,
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      render: (value, record) => <div className="storage-endpoint" title={value}>{value || (record.provider_type === 'file_service' ? '由部署配置托管（待配置）' : '-')}</div>,
    },
    {
      title: 'Bucket',
      dataIndex: 'bucket',
      width: 160,
      render: (value, record) => record.provider_type === 'file_service' ? '内容寻址' : value,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value) => <Tag color={value === 1 ? 'green' : 'default'}>{value === 1 ? '启用' : '停用'}</Tag>,
    },
    {
      title: '密钥',
      dataIndex: 'has_secret_key',
      width: 90,
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? '已配置' : '未配置'}</Tag>,
    },
    {
      title: '操作',
      width: 260,
      render: (_, record) => (
        <div className="storage-actions">
          <Button size="small" onClick={() => testProvider(record)}>测试</Button>
          {!['minio', 'file_service'].includes(record.provider_type) && <Button size="small" onClick={() => openEdit(record)}>编辑</Button>}
          <Button size="small" disabled={record.is_default || record.status !== 1} onClick={() => setDefault(record)}>
            {record.is_default ? '当前启用' : '一键切换'}
          </Button>
          {!['minio', 'file_service'].includes(record.provider_type) && <Popconfirm title="确认删除这条云存储配置？" onConfirm={() => deleteProvider(record)} disabled={record.is_default}>
            <Button size="small" danger disabled={record.is_default}>删除</Button>
          </Popconfirm>}
        </div>
      ),
    },
  ];

  return (
    <div className="storage-config-page">
      <h1 className="page-title">云存储配置</h1>
      <Alert
        className="storage-summary"
        type="info"
        showIcon
        message="存储参数由当前部署环境统一托管。本页面切换后，上传、资源列表、用户端和管理端内容查询都会使用选中的存储，并隐藏另一套存储的数据。"
      />
      <Card bordered={false}>
        <div className="storage-toolbar">
          <Space>
            <CloudServerOutlined />
            <span>当前配置 {items.length} 个</span>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchProviders}>刷新</Button>
          </Space>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={false}
          scroll={{ x: 920 }}
        />
      </Card>

      <Modal
        title={editing ? '编辑云存储' : '新增云存储'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submitForm}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <div className="storage-form-grid">
            <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入名称' }]}>
              <Input placeholder="例如：内置 MinIO" />
            </Form.Item>
            <Form.Item label="类型" name="provider_type" rules={[{ required: true, message: '请选择类型' }]}>
              <Select
                options={[
                  { label: 'MinIO', value: 'minio' },
                  { label: 'S3兼容', value: 's3' },
                  { label: '自定义', value: 'custom' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Endpoint" name="endpoint" rules={[{ required: true, message: '请输入 Endpoint' }]}>
              <Input placeholder="http://127.0.0.1:44187" />
            </Form.Item>
            <Form.Item label="公开访问地址" name="public_endpoint">
              <Input placeholder="留空时使用 Endpoint" />
            </Form.Item>
            <Form.Item label="Bucket" name="bucket" rules={[{ required: true, message: '请输入 Bucket' }]}>
              <Input placeholder="shengyu-projects" />
            </Form.Item>
            <Form.Item label="Region" name="region">
              <Input placeholder="可选" />
            </Form.Item>
            <Form.Item label="Access Key" name="access_key">
              <Input placeholder="Access Key" />
            </Form.Item>
            <Form.Item
              label="Secret Key"
              name="secret_key"
              extra={editing?.has_secret_key ? '已保存密钥，留空不修改。' : undefined}
            >
              <Input.Password placeholder={editing?.has_secret_key ? '留空不修改' : 'Secret Key'} />
            </Form.Item>
            <Form.Item label="HTTPS" name="secure" valuePropName="checked">
              <Switch checkedChildren="是" unCheckedChildren="否" />
            </Form.Item>
            <Form.Item label="启用" name="status" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="停用" />
            </Form.Item>
            <Form.Item className="storage-form-full" label="说明" name="description">
              <Input.TextArea rows={3} maxLength={300} showCount placeholder="用于区分不同云存储服务" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default StorageConfigPage;
